import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Chip,
  Confirm,
  Empty,
  Field,
  Grade,
  MenuButton,
  Modal,
  Pill,
  Room,
  Rows,
  SearchField,
  Picker,
  SkeletonRows,
  Switch,
  TextArea,
  Who,
  useNotices,
} from '../ui';

/**
 * The design system, live — the System canvas rendered from the real
 * components, so what is on this page is what every screen is made of.
 */
export function DesignSheet(): ReactElement {
  return (
    <div className="page stack-lg">
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <span className="eyebrow">Ptitim System</span>
          <h1 className="t-title">Warm neutrals, one green, two signals — and nothing else</h1>
        </div>
      </div>
      <Colour />
      <Type />
      <Buttons />
      <Fields />
      <Objects />
      <Overlays />
      <Feedback />
      <States />
    </div>
  );
}

function Section({
  n,
  title,
  sub,
  children,
}: {
  n: string;
  title: string;
  sub: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="stack">
      <div className="stack" style={{ gap: 2 }}>
        <span className="eyebrow">
          {n} · {title}
        </span>
        <h2 className="t-section">{sub}</h2>
      </div>
      {children}
    </section>
  );
}

const SWATCHES: Array<[string, string, string]> = [
  ['page', '#F7F5F1', 'the page'],
  ['sunk', '#F0EDE6', 'a well'],
  ['card', '#FFFFFF', 'raised, no shadow'],
  ['line', '#E8E4DC', 'separator'],
  ['line-firm', '#C9C2B5', 'a pressable edge'],
  ['muted', '#5C5852', 'secondary text'],
  ['faint', '#6B665E', 'machine facts'],
  ['ink', '#191815', 'ink'],
  ['green', '#0E5B4F', 'settled · chosen · confirmed'],
  ['green-tint', '#E7F0ED', 'selected'],
  ['amber', '#9A6212', 'attention'],
  ['red', '#B3271E', 'destructive'],
];

function Colour(): ReactElement {
  return (
    <Section n="01" title="Colour" sub="Green means settled. It never means clickable.">
      <div className="grid-3">
        {SWATCHES.map(([name, hex, note]) => (
          <div
            key={name}
            className="card"
            style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}
          >
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: `var(--${name})`,
                border: '1px solid var(--line)',
              }}
            />
            <div className="stack" style={{ gap: 2 }}>
              <span className="t-heading" style={{ fontSize: 14 }}>
                --{name}
              </span>
              <span className="mono">
                {hex} · {note}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Type(): ReactElement {
  return (
    <Section n="02" title="Type, space, shape" sub="Two families, one job each">
      <Card>
        <div className="stack-lg">
          <p className="t-title">Page title · 34 / 600 / −3%</p>
          <p className="t-section">Section · 22 / 600</p>
          <p className="t-heading">Card heading · 16 / 600</p>
          <p className="t-body">Body — the size everything a person reads is set in. 14.5 / 400</p>
          <p className="t-secondary">Secondary — context under a row. 13 / 400</p>
          <p className="mono">
            11:42:08 · 9 days · 4 voices — mono 12 / 500, for everything the machine counted
          </p>
          <p className="eyebrow">Eyebrow · mono · .14em</p>
          <p className="t-secondary">
            Plus Jakarta Sans for everything a person wrote or said. JetBrains Mono for everything
            the machine counted. You can tell a claim from a measurement without reading either.
            Spacing is 4-based; card padding 20, gap 16, gutter 32; controls 8, cards 14, pills
            round.
          </p>
        </div>
      </Card>
    </Section>
  );
}

function Buttons(): ReactElement {
  const kinds = [
    ['primary', 'Post'],
    ['secondary', 'Edit'],
    ['quiet', 'Skip'],
    ['danger', 'Delete…'],
  ] as const;
  return (
    <Section n="03" title="Buttons" sub="Four kinds, six states — drawn, not described">
      <Card>
        <div className="stack">
          <div className="row" style={{ padding: '8px 0' }}>
            <span className="eyebrow" style={{ width: 96 }}>
              kind
            </span>
            {['rest', 'disabled', 'working', 'with a key'].map((s) => (
              <span key={s} className="eyebrow" style={{ width: 130 }}>
                {s}
              </span>
            ))}
          </div>
          {kinds.map(([kind, label]) => (
            <div key={kind} className="row" style={{ padding: '8px 0' }}>
              <span className="t-secondary" style={{ width: 96 }}>
                {kind}
              </span>
              <span style={{ width: 130 }}>
                <Button variant={kind}>{label}</Button>
              </span>
              <span style={{ width: 130 }}>
                <Button variant={kind} disabled>
                  {label}
                </Button>
              </span>
              <span style={{ width: 130 }}>
                <Button
                  variant={kind}
                  working={kind === 'primary' ? 'Posting' : kind === 'secondary' ? 'Saving' : label}
                >
                  {label}
                </Button>
              </span>
              <span style={{ width: 130 }}>
                <Button
                  variant={kind}
                  kbd={kind === 'primary' ? 'A' : kind === 'danger' ? '⌫' : '/'}
                >
                  {label}
                </Button>
              </span>
            </div>
          ))}
          <div className="cluster">
            <Button size="lg" variant="primary">
              Large
            </Button>
            <Button>Default</Button>
            <Button size="sm">Small</Button>
            <span className="t-secondary">
              Hover deepens what is visible; no control is created by the pointer. One green button
              per view.
            </span>
          </div>
        </div>
      </Card>
    </Section>
  );
}

function Fields(): ReactElement {
  const [chip, setChip] = useState('All');
  return (
    <Section
      n="04"
      title="Fields, choices, switches"
      sub="You can tell it is a field before you click in it"
    >
      <div className="grid-2">
        <Card title="Text field · five states">
          <div className="stack">
            <Field label="Rest" placeholder="Name this space…" />
            <Field label="Filled" defaultValue="Harbor renewals" />
            <Field
              label="Error"
              defaultValue="harbor renewals"
              error="A space with this name already exists — pick another."
            />
            <Field label="Disabled" defaultValue="maya@harbor-test.com" disabled />
            <TextArea label="Your notes" placeholder="Anything you typed during the call…" />
          </div>
        </Card>
        <Card title="Select · search · choices">
          <div className="stack">
            <DemoPicker />
            <SearchField placeholder="Search everything" />
            <Checkbox label="Record my one-to-ones" defaultChecked />
            <Checkbox label="Disabled, and says so" disabled />
            <Switch label="Save the video" defaultChecked />
            <div className="chips">
              {[
                ['All', 9],
                ['Mine', 4],
                ['Theirs', 5],
                ['Ageing', 3],
                ['No owner', 2],
              ].map(([label, count]) => (
                <Chip
                  key={String(label)}
                  on={chip === label}
                  count={count}
                  onClick={() => setChip(String(label))}
                >
                  {label}
                </Chip>
              ))}
            </div>
            <div className="subtabs">
              {['Summary', 'Note', 'Transcript'].map((t, i) => (
                <span key={t} className={`tab ${i === 1 ? 'on' : ''}`}>
                  {t}
                </span>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </Section>
  );
}

function Objects(): ReactElement {
  return (
    <Section n="05" title="The product's own objects" sub="Person · evidence · commitment">
      <div className="grid-2">
        <Card title="Person · four states, and a room">
          <div className="cluster">
            <Who name="Priya Raman" email="priya.raman@meridian-test.com" />
            <Who name="Alex Renner" email="alex.renner@meridian-test.com" me />
            <Who name={null} email="tom.okafor@meridian-test.com" />
            <Who name={null} email="" />
            <Room name="Harbor weekly" />
          </div>
          <p className="t-secondary" style={{ marginTop: 12 }}>
            Round for a human, rounded-square for a room or a company — you can tell them apart at
            any size. A name is never a bare word.
          </p>
        </Card>
        <Card title="Evidence grade — a ramp you can read in greyscale">
          <div className="rows">
            {(
              [
                ['HEARD', 'the app pulled it out of the talking'],
                ['ASSUMED', 'it worked out the owner itself'],
                ['WRITTEN', 'you typed it during the call'],
                ['CONFIRMED', 'a person said yes to it'],
              ] as const
            ).map(([g, why]) => (
              <div key={g} className="row dense" style={{ padding: '8px 0' }}>
                <span style={{ width: 96 }}>
                  <Grade value={g} />
                </span>
                <span className="t-secondary">{why}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Card title="Commitment row — every state it has" flush>
        <Rows>
          {(
            [
              ['rest', 'WRITTEN', '9 days', 'Mark done'],
              ['ageing', 'ASSUMED', '23 days', 'Mark done'],
              ['needs an owner', 'HEARD', '2 days', 'Give an owner'],
              ['done', 'CONFIRMED', 'today', 'Undo'],
            ] as const
          ).map(([state, grade, age, primary]) => (
            <div key={state} className={`row ${state === 'done' ? 'leaving' : ''}`}>
              <div className="row-main">
                <span className="row-title">
                  Send Rafał the migration plan with the rollback section filled in
                </span>
                <span className="row-sub">
                  {state === 'needs an owner' ? (
                    <Who name={null} email="" />
                  ) : (
                    <Who name="Priya Raman" email="priya.raman@meridian-test.com" />
                  )}
                  <span className="mono">Platform standup · Wed 09:21</span>
                  <span className={`mono ${state === 'ageing' ? 'amber' : ''}`}>{age}</span>
                  <Grade value={grade} />
                </span>
              </div>
              <div className="row-actions">
                <Button size="sm" variant={state === 'needs an owner' ? 'primary' : 'secondary'}>
                  {primary}
                </Button>
                <Button size="sm" variant="quiet" aria-label="More">
                  ···
                </Button>
              </div>
            </div>
          ))}
        </Rows>
      </Card>
    </Section>
  );
}

function Overlays(): ReactElement {
  const [modal, setModal] = useState(false);
  const [confirm, setConfirm] = useState(false);
  return (
    <Section n="06" title="Overlays" sub="Menu · confirm · modal — one deep shadow">
      <Card>
        <div className="cluster">
          <MenuButton
            label="Menu"
            items={[
              { label: 'Rename' },
              { label: 'Share…' },
              { label: 'Move to a space', disabledBecause: 'only its owner may' },
              { label: 'Delete…', danger: true },
            ]}
          />
          <Button onClick={() => setModal(true)}>Open a modal</Button>
          <Button variant="danger" onClick={() => setConfirm(true)}>
            Delete…
          </Button>
          <span className="tooltip">In the call · 14:32</span>
        </div>
      </Card>
      {modal ? (
        <Modal
          title="Share this meeting"
          onClose={() => setModal(false)}
          foot={<Button onClick={() => setModal(false)}>Done</Button>}
        >
          <Field placeholder="Add a person or a group…" />
          <Rows>
            <div className="row" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <Who name="Priya Raman" email="priya.raman@meridian-test.com" />
              <span className="t-secondary grow">was in the meeting</span>
              <Pill>can edit</Pill>
            </div>
            <div className="row" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <Who name="Nils Aaberg" email="nils.aaberg@harbor-test.com" />
              <span className="t-secondary grow">outside your company</span>
              <Pill>can read</Pill>
            </div>
          </Rows>
        </Modal>
      ) : null}
      {confirm ? (
        <Confirm
          title="Delete this meeting?"
          noun="Delete the meeting"
          onConfirm={() => setConfirm(false)}
          onClose={() => setConfirm(false)}
        >
          The transcript, the note and the 4 commitments it produced go with it. Two of those
          commitments are open, and one is owed to you by Priya. This cannot be undone.
        </Confirm>
      ) : null}
    </Section>
  );
}

function Feedback(): ReactElement {
  const { show } = useNotices();
  return (
    <Section
      n="07"
      title="Feedback"
      sub="One notice slot, four tones, and an undo that is always reachable"
    >
      <Card>
        <div className="cluster">
          <Button
            onClick={() =>
              show({
                tone: 'success',
                text: 'Marked done — “Send Rafał the migration plan” has left your side of the ledger.',
                action: { label: 'Undo', onSelect: () => undefined },
              })
            }
          >
            Success
          </Button>
          <Button
            onClick={() =>
              show({
                tone: 'attention',
                text: 'Roadmap review wasn’t recorded — the notetaker waited five minutes and nobody admitted it.',
              })
            }
          >
            Attention
          </Button>
          <Button
            onClick={() =>
              show({
                tone: 'failure',
                text: 'This page could not load. Nothing was lost — it is all on the server.',
                action: { label: 'Try again', onSelect: () => undefined },
              })
            }
          >
            Failure
          </Button>
          <Button
            onClick={() =>
              show({
                id: 'working',
                tone: 'working',
                text: 'Writing up Platform standup — about 40 seconds left.',
                ttlMs: 4000,
              })
            }
          >
            Working
          </Button>
          <span className="t-secondary">They appear in the slot under the header.</span>
        </div>
      </Card>
    </Section>
  );
}

function States(): ReactElement {
  return (
    <Section
      n="08"
      title="Empty, loading, and broken"
      sub="The three states that decide whether the product feels finished"
    >
      <div className="grid-3">
        <Card flush>
          <Empty tone="ok" lead="Nothing outstanding between you and anyone.">
            Both columns are empty and both are meant to be. This is the state the product is for.
          </Empty>
        </Card>
        <Card flush>
          <Empty
            lead="Nothing here yet — your first recorded meeting is in 40 minutes."
            action={<Button size="sm">See the week</Button>}
          >
            Empty because nothing has happened, not because something is missing.
          </Empty>
        </Card>
        <Card flush>
          <SkeletonRows rows={3} />
        </Card>
      </div>
    </Section>
  );
}

/** The app's select, live: a field at rest, the menu when open (ui/Picker). */
function DemoPicker(): ReactElement {
  const [language, setLanguage] = useState('en');
  return (
    <Picker
      label="Transcription language"
      hint="A series can override this."
      value={language}
      onChange={setLanguage}
      width={260}
      options={[
        { value: 'auto', label: 'Set by the series' },
        { value: 'en', label: 'English' },
        { value: 'ru', label: 'Russian' },
        { value: 'de', label: 'German' },
      ]}
    />
  );
}
